import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { TopMomentsSection } from '@/components/public/TopMomentsSection';
import type {
  ClassroomPublicConfig,
  ClassroomTopMomentsConfig,
  ClassroomVisibility,
  ClassroomQuote,
} from '@/types/classrooms';
import { useAuth } from '@/lib/auth-context';
import { supabase } from '@/supabase/client';

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';

const defaultVisibility: ClassroomVisibility = {
  isPublic: false,
  showShareLink: false,
  showInCommunityRotation: false,
};

const defaultTopMoments: ClassroomTopMomentsConfig = {
  maxSlots: 5,
  items: [],
};

const defaultPublicConfig: ClassroomPublicConfig = {
  displayName: '',
  about: '',
  quote: undefined,
  goals: [],
  wishlist: [],
  theme: { style: 'clean', layout: 'classic' },
  topMoments: defaultTopMoments,
  visibility: defaultVisibility,
};

type ClassroomRow = {
  id: string;
  name: string;
  grade_level?: string | null;
  public_config?: unknown;
};

type MembershipRow = {
  classroom_id?: string;
};

type ShareLinkRow = {
  code: string;
  status: string;
};

type WishlistItem = NonNullable<ClassroomPublicConfig['wishlist']>[number];

type AvailablePhoto = {
  id: string;
  url: string;
  caption?: string;
};

export default function TeacherPublicClassroomPage() {
  const { user, session } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [classroom, setClassroom] = useState<ClassroomRow | null>(null);
  const [publicConfig, setPublicConfig] = useState<ClassroomPublicConfig>(defaultPublicConfig);
  const [shareLink, setShareLink] = useState<ShareLinkRow | null>(null);
  const [availablePhotos, setAvailablePhotos] = useState<AvailablePhoto[]>([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);

  const canSubmit = useMemo(() => Boolean(classroom?.id && !saving), [classroom?.id, saving]);

  const publicPageUrl = useMemo(() => {
    if (!classroom?.id) return null;
    const base = typeof window !== 'undefined' ? window.location.origin : 'https://projecttower.org';
    return `${base}/c/${classroom.id}`;
  }, [classroom?.id]);

  useEffect(() => {
    if (!user?.id) return;

    const normalizeConfig = (cfg: ClassroomPublicConfig | null | undefined): ClassroomPublicConfig => {
      const top = (cfg?.topMoments ?? (cfg as any)?.top_moments) as ClassroomTopMomentsConfig | undefined;
      const vis = (cfg?.visibility ?? (cfg as any)?.visibility) as ClassroomVisibility | undefined;
      return {
        ...defaultPublicConfig,
        ...cfg,
        displayName: cfg?.displayName ?? '',
        quote: cfg?.quote ?? undefined,
        goals: Array.isArray(cfg?.goals) ? cfg?.goals : [],
        wishlist: Array.isArray(cfg?.wishlist) ? cfg?.wishlist : [],
        theme: { ...defaultPublicConfig.theme, ...(cfg?.theme ?? {}) },
        topMoments: {
          ...defaultTopMoments,
          ...(top ?? {}),
          items: Array.isArray(top?.items) ? top.items : [],
        },
        visibility: {
          ...defaultVisibility,
          ...(vis ?? {}),
        },
      };
    };

    const loadClassroom = async () => {
      setLoading(true);
      setError(null);

      const fetchById = async (classroomId: string | null) => {
        if (!classroomId) return null;
        const { data, error: classErr } = await supabase
          .from('classrooms')
          .select('id, name, grade_level, public_config')
          .eq('id', classroomId)
          .maybeSingle();
        if (classErr) throw classErr;
        return data as ClassroomRow | null;
      };

      try {
        let row: ClassroomRow | null = null;
        const { data: primary, error: primaryErr } = await supabase
          .from('classrooms')
          .select('id, name, grade_level, public_config')
          .eq('teacher_id', user.id)
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();

        if (primaryErr) throw primaryErr;
        row = (primary as ClassroomRow | null) ?? null;

        if (!row) {
          const { data: membership, error: membershipErr } = await supabase
            .from('classroom_members')
            .select('classroom_id')
            .eq('user_id', user.id)
            .eq('role', 'teacher')
            .order('created_at', { ascending: true })
            .limit(1)
            .maybeSingle();

          if (membershipErr) throw membershipErr;

          const membershipRow = membership as MembershipRow | null;
          if (membershipRow?.classroom_id) {
            row = await fetchById(membershipRow.classroom_id);
          }
        }

        if (!row) {
          setError('No classroom found for this teacher account.');
          setClassroom(null);
          setPublicConfig(defaultPublicConfig);
          return;
        }

        setClassroom(row);
        setPublicConfig(normalizeConfig((row.public_config ?? {}) as ClassroomPublicConfig));

        // Fetch active share link
        const { data: shareData } = await supabase
          .from('share_links')
          .select('code, status')
          .eq('owner_id', user.id)
          .eq('kind', 'teacher')
          .eq('status', 'active')
          .limit(1)
          .maybeSingle();

        if (shareData) {
          setShareLink(shareData as ShareLinkRow);
        }
      } catch (err: any) {
        const message = err?.message || 'Failed to load classroom';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    loadClassroom();
  }, [user?.id]);

  // Load teacher photos for Top Moments selection
  useEffect(() => {
    if (!user?.id) return;

    const loadPhotos = async () => {
      setLoadingPhotos(true);
      try {
        const { data: photos, error: photosErr } = await supabase
          .from('teacher_photos')
          .select('id, file_url, caption, approved')
          .eq('teacher_id', user.id)
          .eq('approved', true)
          .order('created_at', { ascending: false })
          .limit(50);

        if (photosErr) {
          console.warn('Failed to load teacher photos:', photosErr);
          return;
        }

        // Generate signed URLs for photos
        const photosWithUrls: AvailablePhoto[] = await Promise.all(
          (photos || []).map(async (photo) => {
            let url = photo.file_url;
            // If file_url is a storage path, get a public URL
            if (photo.file_url && !photo.file_url.startsWith('http')) {
              const { data: urlData } = await supabase.storage
                .from('photos')
                .createSignedUrl(photo.file_url, 3600); // 1 hour expiry
              if (urlData?.signedUrl) {
                url = urlData.signedUrl;
              }
            }
            return {
              id: photo.id,
              url: url || '',
              caption: photo.caption || undefined,
            };
          })
        );

        setAvailablePhotos(photosWithUrls.filter((p) => p.url));
      } catch (err) {
        console.warn('Failed to load photos for Top Moments:', err);
      } finally {
        setLoadingPhotos(false);
      }
    };

    loadPhotos();
  }, [user?.id]);

  const handleWishlistChange = (idx: number, patch: Partial<WishlistItem>) => {
    setPublicConfig((prev) => {
      const current = prev.wishlist ?? [];
      const next = [...current];
      const item = next[idx] || {};
      next[idx] = { ...item, ...patch } as WishlistItem;
      return { ...prev, wishlist: next };
    });
  };

  const handleVisibilityChange = (key: keyof ClassroomVisibility, value: boolean) => {
    setPublicConfig((prev) => ({
      ...prev,
      visibility: {
        ...defaultVisibility,
        ...(prev.visibility ?? {}),
        [key]: value,
      },
    }));
  };

  const handleQuoteChange = (patch: Partial<ClassroomQuote>) => {
    setPublicConfig((prev) => ({
      ...prev,
      quote: {
        text: prev.quote?.text ?? '',
        attribution: prev.quote?.attribution,
        ...patch,
      },
    }));
  };

  const handleSave = async () => {
    if (!classroom?.id || !session?.access_token) return;
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${CORE_API}/api/v1/classrooms/${classroom.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ public_config: publicConfig }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body?.error?.message || body?.error || 'Failed to save public classroom config');
      }

      const result = await response.json();
      setSuccess((result.data?.message) || 'Public classroom updated');
    } catch (err: any) {
      const message = err?.message || 'Failed to save public classroom config';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6">Loading…</div>;
  if (!classroom) return <div className="p-6">Classroom not found.</div>;

  const visibility = publicConfig.visibility ?? defaultVisibility;

  return (
    <div className="mx-auto max-w-4xl space-y-6 py-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">Public classroom page</h1>
        <p className="text-sm text-gray-500">
          Curate the public page families and sponsors can see for your classroom.
        </p>
      </header>

      {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>}
      {success && <div className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{success}</div>}

      {/* Visibility Controls */}
      <section className="space-y-4 rounded-lg border border-blue-200 bg-blue-50/50 p-4">
        <div>
          <h2 className="font-medium text-blue-900">Visibility settings</h2>
          <p className="text-xs text-blue-700">Control who can see your classroom and how it appears.</p>
        </div>

        <div className="space-y-3">
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={visibility.isPublic}
              onChange={(e) => handleVisibilityChange('isPublic', e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300"
            />
            <div>
              <span className="font-medium text-gray-900">Make classroom public</span>
              <p className="text-xs text-gray-500">
                When enabled, anyone with your share link can view your classroom page.
              </p>
            </div>
          </label>

          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={visibility.showShareLink}
              onChange={(e) => handleVisibilityChange('showShareLink', e.target.checked)}
              disabled={!visibility.isPublic}
              className="mt-1 h-4 w-4 rounded border-gray-300 disabled:opacity-50"
            />
            <div className={!visibility.isPublic ? 'opacity-50' : ''}>
              <span className="font-medium text-gray-900">Display gift/support link on public page</span>
              <p className="text-xs text-gray-500">
                Show the option for visitors to gift credits or donate to your classroom.
              </p>
            </div>
          </label>

          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={visibility.showInCommunityRotation}
              onChange={(e) => handleVisibilityChange('showInCommunityRotation', e.target.checked)}
              disabled={!visibility.isPublic}
              className="mt-1 h-4 w-4 rounded border-gray-300 disabled:opacity-50"
            />
            <div className={!visibility.isPublic ? 'opacity-50' : ''}>
              <span className="font-medium text-gray-900">Appear in community rotation</span>
              <p className="text-xs text-gray-500">
                Your classroom will be featured in rotation on the community page, helping sponsors discover you.
              </p>
            </div>
          </label>
        </div>

        {publicPageUrl && visibility.isPublic && (
          <div className="rounded-md bg-white p-3 text-sm">
            <p className="text-xs text-gray-500 mb-1">Your public classroom URL:</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded bg-gray-100 px-2 py-1 text-xs">{publicPageUrl}</code>
              <button
                type="button"
                onClick={() => navigator.clipboard.writeText(publicPageUrl)}
                className="rounded bg-blue-600 px-2 py-1 text-xs text-white hover:bg-blue-700"
              >
                Copy
              </button>
              <Link
                to={`/c/${classroom?.id}`}
                target="_blank"
                className="rounded border border-gray-300 px-2 py-1 text-xs hover:bg-gray-50"
              >
                Preview
              </Link>
            </div>
          </div>
        )}
      </section>

      {/* Classroom Display Name */}
      <section className="space-y-2 rounded-lg border p-4">
        <h2 className="font-medium">Classroom display name</h2>
        <p className="text-xs text-gray-500">
          A custom name for your classroom (e.g., "Mrs. Smith's 3rd Grade Stars"). Defaults to "{classroom.name}" if not set.
        </p>
        <input
          className="w-full rounded-md border px-3 py-2 text-sm"
          placeholder={classroom.name}
          value={publicConfig.displayName ?? ''}
          onChange={(e) => setPublicConfig((prev) => ({ ...prev, displayName: e.target.value }))}
        />
      </section>

      {/* About Section */}
      <section className="space-y-2 rounded-lg border p-4">
        <h2 className="font-medium">About your classroom</h2>
        <p className="text-xs text-gray-500">A short description that appears on your public page.</p>
        <textarea
          className="w-full rounded-md border p-2 text-sm"
          rows={4}
          value={publicConfig.about ?? ''}
          onChange={(e) => setPublicConfig((prev) => ({ ...prev, about: e.target.value }))}
        />
      </section>

      {/* Quote Section */}
      <section className="space-y-2 rounded-lg border p-4">
        <h2 className="font-medium">Classroom quote or motto</h2>
        <p className="text-xs text-gray-500">
          Share an inspiring quote, class motto, or memorable saying from your classroom.
        </p>
        <textarea
          className="w-full rounded-md border p-2 text-sm"
          rows={2}
          placeholder="Enter your quote or motto..."
          value={publicConfig.quote?.text ?? ''}
          onChange={(e) => handleQuoteChange({ text: e.target.value })}
        />
        <input
          className="w-full rounded-md border px-3 py-2 text-sm"
          placeholder="Attribution (optional) — e.g., 'Class motto' or 'Student quote'"
          value={publicConfig.quote?.attribution ?? ''}
          onChange={(e) => handleQuoteChange({ attribution: e.target.value })}
        />
      </section>

      {/* Goals Section */}
      <section className="space-y-2 rounded-lg border p-4">
        <h2 className="font-medium">Class goals</h2>
        <p className="text-xs text-gray-500">What you and your students are working toward this year.</p>
        <div className="space-y-2">
          {(publicConfig.goals ?? []).map((goal, idx) => (
            <div key={idx} className="flex gap-2">
              <input
                className="flex-1 rounded-md border px-2 py-1 text-sm"
                value={goal}
                onChange={(e) => {
                  const next = [...(publicConfig.goals ?? [])];
                  next[idx] = e.target.value;
                  setPublicConfig((prev) => ({ ...prev, goals: next }));
                }}
              />
              <button
                type="button"
                className="text-xs text-red-500"
                onClick={() => {
                  const next = [...(publicConfig.goals ?? [])];
                  next.splice(idx, 1);
                  setPublicConfig((prev) => ({ ...prev, goals: next }));
                }}
              >
                Remove
              </button>
            </div>
          ))}

          <button
            type="button"
            className="text-xs text-blue-600"
            onClick={() => setPublicConfig((prev) => ({ ...prev, goals: [...(prev.goals ?? []), ''] }))}
          >
            + Add goal
          </button>
        </div>
      </section>

      {/* Wishlist Section */}
      <section className="space-y-2 rounded-lg border p-4">
        <h2 className="font-medium">Wishlist</h2>
        <p className="text-xs text-gray-500">Share items or links supporters can help with.</p>
        <div className="space-y-3">
          {(publicConfig.wishlist ?? []).map((item, idx) => (
            <div key={idx} className="flex flex-col gap-2 rounded-md bg-gray-50 p-3 sm:flex-row sm:items-center">
              <input
                className="flex-1 rounded-md border px-2 py-1 text-sm"
                placeholder="Item label"
                value={item.label}
                onChange={(e) => handleWishlistChange(idx, { label: e.target.value })}
              />
              <input
                className="flex-1 rounded-md border px-2 py-1 text-sm"
                placeholder="Optional URL"
                value={item.url ?? ''}
                onChange={(e) => handleWishlistChange(idx, { url: e.target.value || null })}
              />
              <button
                type="button"
                className="text-xs text-red-500"
                onClick={() =>
                  setPublicConfig((prev) => {
                    const next = [...(prev.wishlist ?? [])];
                    next.splice(idx, 1);
                    return { ...prev, wishlist: next };
                  })
                }
              >
                Remove
              </button>
            </div>
          ))}

          <button
            type="button"
            className="text-xs text-blue-600"
            onClick={() =>
              setPublicConfig((prev) => ({
                ...prev,
                wishlist: [...(prev.wishlist ?? []), { label: '', url: null }],
              }))
            }
          >
            + Add wishlist item
          </button>
        </div>
      </section>

      {/* Top Moments Section */}
      <section className="space-y-3 rounded-lg border p-4">
        <h2 className="font-medium">Top moments of the year</h2>
        <p className="text-xs text-gray-500">
          Choose up to 3, 5, 7, or 10 special moments to showcase on your public page. You can reorder them.
        </p>

        {loadingPhotos && (
          <p className="text-xs text-gray-500">Loading your photos...</p>
        )}

        <TopMomentsSection
          mode="edit"
          value={publicConfig.topMoments}
          availablePhotos={availablePhotos}
          onChange={(next) => setPublicConfig((prev) => ({ ...prev, topMoments: next }))}
        />

        {!loadingPhotos && availablePhotos.length === 0 && (
          <p className="text-xs text-amber-600">
            No approved photos available. Upload photos in the Overview section to attach them to moments.
          </p>
        )}
      </section>

      {/* Theme Section */}
      <section className="space-y-3 rounded-lg border p-4">
        <h2 className="font-medium">Page style</h2>
        <p className="text-xs text-gray-500">Choose how your public classroom page looks.</p>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {(['clean', 'playful', 'bold', 'minimal'] as const).map((style) => (
            <button
              key={style}
              type="button"
              onClick={() =>
                setPublicConfig((prev) => ({
                  ...prev,
                  theme: { ...prev.theme, style },
                }))
              }
              className={`rounded-lg border p-3 text-sm capitalize transition ${
                publicConfig.theme?.style === style
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {style}
            </button>
          ))}
        </div>

        <div className="pt-2">
          <label className="text-xs text-gray-500">Accent color</label>
          <div className="mt-1 flex items-center gap-2">
            <input
              type="color"
              value={publicConfig.theme?.accentColor ?? '#3b82f6'}
              onChange={(e) =>
                setPublicConfig((prev) => ({
                  ...prev,
                  theme: { ...prev.theme, accentColor: e.target.value },
                }))
              }
              className="h-8 w-12 cursor-pointer rounded border"
            />
            <span className="text-xs text-gray-500">{publicConfig.theme?.accentColor ?? '#3b82f6'}</span>
          </div>
        </div>

        <div className="pt-2">
          <label className="text-xs text-gray-500">Layout style (more coming with AI tools)</label>
          <div className="mt-1 grid grid-cols-2 gap-2 sm:grid-cols-3">
            {(['classic', 'pinboard', 'timeline'] as const).map((layout) => (
              <button
                key={layout}
                type="button"
                onClick={() =>
                  setPublicConfig((prev) => ({
                    ...prev,
                    theme: { ...prev.theme, layout },
                  }))
                }
                className={`rounded-lg border p-2 text-xs capitalize transition ${
                  (publicConfig.theme?.layout ?? 'classic') === layout
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {layout}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-gray-400">
            More layouts like collage, storybook, and interactive coming soon!
          </p>
        </div>
      </section>

      <div className="flex justify-end gap-3">
        {publicPageUrl && visibility.isPublic && (
          <Link
            to={`/c/${classroom?.id}`}
            target="_blank"
            className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
          >
            Preview public page
          </Link>
        )}
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSubmit}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save public page'}
        </button>
      </div>
    </div>
  );
}
